import React from 'react';
import { Box, Typography, Paper, Divider, Grid, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { normalizeTelemetryRecord } from '../utils/telemetryRecordNormalizer';

/**
 * Datalens component displays all fields of a telemetry record in a structured, sectioned layout.
 * - If modal=true, renders as a centered popup with a close button and dimmed background.
 * - If modal=false, renders as a static panel (for Canopy Telemetry view).
 *
 * Props:
 *   record: object (the telemetry record to display)
 *   open: boolean (modal only, whether to show)
 *   onClose: function (modal only, called when closed)
 *   modal: boolean (true for modal/popup, false for static)
 *   columns: number (optional, number of columns to use for rendering)
 */

// Helper to render a label-value pair in a grid cell
function DataGridCell({ label, value, columns, valueSx, tooltip }) {
  // xs should be 12/columns (e.g., 3 for 4 columns, 4 for 3 columns)
  const xs = Math.floor(12 / columns);
  // Font size: 15px for 4 columns, 13px otherwise
  const fontSize = columns === 4 ? 15 : 13;
  return (
    <Grid item xs={xs} sx={{ mb: 0, pr: 1 }}>
      {tooltip ? (
        <Tooltip title={tooltip} arrow placement="top" enterDelay={250}>
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{ fontSize, lineHeight: 1.1, mb: 0, cursor: 'help' }}
          >
            {label}
          </Typography>
        </Tooltip>
      ) : (
        <Typography variant="caption" color="textSecondary" sx={{ fontSize, lineHeight: 1.1, mb: 0 }}>{label}</Typography>
      )}
      <Typography
        variant="body2"
        sx={{ fontSize, fontWeight: 500, ml: 0, lineHeight: 1.1, mb: 0, ...(valueSx || {}) }}
      >
        {value !== undefined && value !== null && value !== '' ? value : '—'}
      </Typography>
    </Grid>
  );
}

function Datalens({ record: rawRecord, open = false, onClose, modal = false, columns = 3 }) {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  // Responsive column override: 2 on phone, 3 on tablet, caller's value on desktop
  const responsiveColumns = isPhone ? 2 : isTablet ? 3 : columns;

  const record = normalizeTelemetryRecord(rawRecord);
  if (!record) return null;

  const toNumberOrNull = (v) => {
    if (v === null || v === undefined) return null;
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
    return null;
  };

  const formatMaybe = (v) => {
    if (v === undefined || v === null || v === '') return '—';
    return v;
  };

  const formatDegrees100 = (raw) => {
    const n = toNumberOrNull(raw);
    if (n === null) return '—';
    const deg = n / 100;
    return `${deg.toFixed(2)}°`;
  };

  const formatInches100 = (raw) => {
    const n = toNumberOrNull(raw);
    if (n === null) return '—';
    const inches = n / 100;
    return `${inches.toFixed(2)} in`;
  };

  const formatSecsSinceMidnightToTime = (rawSecs, recordTimestamp, timeZone = 'America/Nassau') => {
    const secs = toNumberOrNull(rawSecs);
    if (secs === null) return '—';

    const ts = typeof recordTimestamp === 'string' ? recordTimestamp : null;
    const base = ts ? new Date(ts) : new Date();
    if (Number.isNaN(base.getTime())) return '—';

    // Interpret secs as "seconds since midnight UTC" on the record's date.
    const y = base.getUTCFullYear();
    const m = base.getUTCMonth();
    const d = base.getUTCDate();
    const utcMidnight = new Date(Date.UTC(y, m, d, 0, 0, 0));
    const dt = new Date(utcMidnight.getTime() + secs * 1000);

    try {
      return new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).format(dt);
    } catch {
      // Fallback if Intl/timeZone is unavailable in the runtime
      return dt.toISOString().slice(11, 19);
    }
  };

  // Helper to get value by possible keys
  const get = (obj, keys, fallback = '—') => {
    for (const key of keys) {
      if (obj && obj[key] !== undefined && obj[key] !== null) return obj[key];
    }
    return fallback;
  };

  // Extract the sabalController data from the nested structure
  const sabalController = record.data?.sabalController || record.payload?.sabalController || record.sabalController || {};
  const systemInfo = sabalController.systemInfo || {};

  // Canopy state text fallback:
  // Prefer controller-provided canopyStateText, but if it's missing/blank and the numeric
  // canopyState is one of the new values, render the correct label.
  const canopyStateTextValue = (() => {
    const rawText = get(systemInfo, ['canopyStateText'], null);
    if (typeof rawText === 'string' && rawText.trim() !== '') return rawText;

    const stateNum = toNumberOrNull(get(systemInfo, ['canopyState'], null));
    if (stateNum === 13) return 'CanopyFlatW';
    if (stateNum === 14) return 'CanopyPackedW';
    return '—';
  })();

  // Header fields (always visible) - support both snake_case and camelCase
  const headerFields = [
    { label: 'Fleet ID', value: get(record, ['fleet_id', 'fleetId']), tooltip: 'Source: record.fleet_id' },
    { label: 'Device ID', value: get(record, ['device_id', 'deviceId']), tooltip: 'Source: record.device_id' },
    { label: 'Canopy ID', value: get(record, ['canopy_id', 'canopyId']), tooltip: 'Source: record.canopy_id (P6200 Unit ID)' },
    { label: 'Message ID', value: get(record, ['message_id', 'messageId']), tooltip: 'Source: record.message_id (controller-generated message id)' },
    { label: 'Sequence Number', value: get(record, ['sequence_number', 'sequenceNumber']), tooltip: 'Source: record.sequence_number (monotonic counter from controller)' },
    { label: 'Timestamp', value: get(record, ['timestamp', 'observedAt', 'observed_at']), tooltip: 'Source: record.timestamp (UTC from controller payload)' },
    { label: 'Created At', value: get(record, ['created_at', 'createdAt', 'receivedAt', 'received_at']), tooltip: 'Source: record.created_at (DB insert time)' },
  ];

  // System Info fields - these come from sabalController.systemInfo
  const systemInfoFields = [
    { label: 'System Mode', value: get(systemInfo, ['systemMode']), tooltip: 'Source: sabalController.systemInfo.systemMode (enum numeric code)' },
    { label: 'System Mode Text', value: get(systemInfo, ['systemModeText']), tooltip: 'Source: sabalController.systemInfo.systemModeText (human-readable)' },
    { label: 'System State', value: get(systemInfo, ['systemState']), tooltip: 'Source: sabalController.systemInfo.systemState (enum numeric code)' },
    { label: 'System State Text', value: get(systemInfo, ['systemStateText']), tooltip: 'Source: sabalController.systemInfo.systemStateText (human-readable)' },
    { label: 'Canopy State', value: get(systemInfo, ['canopyState']), tooltip: 'Source: sabalController.systemInfo.canopyState (enum numeric code)' },
    { label: 'Canopy State Text', value: canopyStateTextValue, tooltip: 'Source: sabalController.systemInfo.canopyStateText (human-readable). Fallbacks: 13=CanopyFlatW, 14=CanopyPackedW when canopyStateText is missing.' },
    { label: 'Last Update Time', value: get(systemInfo, ['lastUpdateTime']), tooltip: 'Source: sabalController.systemInfo.lastUpdateTime (UTC)' },
  ];

  // VFD Status - these come from sabalController.vfdStatus
  const vfdStatus = sabalController.vfdStatus || {};
  const vfdMotors = [
    { key: 'am1', label: 'AM1' },
    { key: 'am2', label: 'AM2' },
    { key: 'tm1', label: 'TM1' },
    { key: 'tm2', label: 'TM2' }
  ];

  const decodeVfdStateWord = (state) => {
    const n = toNumberOrNull(state);
    if (n === null) return { hex: '—', health: '—', fault: '—', faultHex: '—' };
    const u32 = (n >>> 0);
    const hex = `0x${u32.toString(16).toUpperCase().padStart(8, '0')}`;
    const err = (u32 & 0xFFFF) >>> 0;
    const faultHex = `0x${err.toString(16).toUpperCase().padStart(4, '0')}`;

    const flags = [];
    if ((u32 & 0x80000000) !== 0) flags.push('FOUND');
    if ((u32 & 0x40000000) !== 0) flags.push('WRONG_MODEL');
    if ((u32 & 0x20000000) !== 0) flags.push('INIT_FAIL');
    if ((u32 & 0x10000000) !== 0) flags.push('COMMS_FAIL');
    const health = flags.length ? flags.join(' | ') : 'OK';

    const fault = err !== 0 ? `FAULT ${faultHex}` : 'OK';
    return { hex, health, fault, faultHex };
  };

  const decodeEncoderStateWord = (state) => {
    const n = toNumberOrNull(state);
    if (n === null) return { hex: '—', health: '—', fault: '—', faultHex: '—' };
    const u32 = (n >>> 0);
    const hex = `0x${u32.toString(16).toUpperCase().padStart(8, '0')}`;
    const err = (u32 & 0xFFFF) >>> 0;
    const faultHex = `0x${err.toString(16).toUpperCase().padStart(4, '0')}`;

    // Mirror controller state-flag pattern used across devices:
    // 0x80000000 FOUND/OK, 0x40000000 wrong/invalid, 0x20000000 init fail, 0x10000000 comms fail.
    const flags = [];
    if ((u32 & 0x80000000) !== 0) flags.push('FOUND');
    if ((u32 & 0x40000000) !== 0) flags.push('INVALID');
    if ((u32 & 0x20000000) !== 0) flags.push('INIT_FAIL');
    if ((u32 & 0x10000000) !== 0) flags.push('COMMS_FAIL');
    const health = flags.length ? flags.join(' | ') : 'OK';

    const fault = err !== 0 ? `FAULT ${faultHex}` : 'OK';
    return { hex, health, fault, faultHex };
  };

  const formatScaled = (raw, scale, suffix) => {
    const n = toNumberOrNull(raw);
    if (n === null) return '—';
    const v = n / scale;
    // Keep output compact but readable
    const s = Number.isInteger(v) ? String(v) : v.toFixed(2).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
    return `${s}${suffix ? ` ${suffix}` : ''}`.trim();
  };

  const formatWindSpeedMps = (raw) => {
    const n = toNumberOrNull(raw);
    if (n === null) return '—';
    // Controller publishes tenths of m/s (e.g. 17 => 1.7 m/s)
    const mps = n / 10;
    const s = mps.toFixed(1).replace(/\.0$/, '');
    return `${s} m/s`;
  };

  const formatWindDirDeg = (raw) => {
    const n = toNumberOrNull(raw);
    if (n === null) return '—';
    // Observed on P6200 and in sabal-app: if raw > 360 then it's degrees*10 (e.g. 3150 => 315.0°)
    const v = n > 360 ? (n / 10) : n;
    const norm = ((v % 360) + 360) % 360;
    if (n > 360) return `${norm.toFixed(1)}°`;
    return `${Math.round(norm)}°`;
  };

  const formatWindLimits = (raw) => {
    const n = toNumberOrNull(raw);
    if (n === null) return '—';
    if (n === 0) return 'Normal (0)';
    if (n === 1) return 'Above Flat Limit (1)';
    if (n === 2) return 'Above Pack Limit (2)';
    return `${n}`;
  };

  const decodeAnemometerStateWord = (state) => {
    const n = toNumberOrNull(state);
    if (n === null) return { hex: '—', text: '—' };
    const u32 = (n >>> 0);
    const hex = `0x${u32.toString(16).toUpperCase().padStart(8, '0')}`;
    const found = (u32 & 0x80000000) !== 0;
    const notFound = (u32 & 0x20000000) !== 0;
    const lostComms = (u32 & 0x10000000) !== 0;
    if (found || (u32 === 0 && sabalController.operating?.wind?.sensorHealthy !== false)) {
      return { hex, text: 'Ok' };
    }
    if (notFound) return { hex, text: 'Not Found' };
    if (lostComms) return { hex, text: 'Lost Comms' };
    return { hex, text: 'Fault' };
  };

  const vfdHeaderTooltips = {
    Metric: 'Row label describing the metric shown across the VFD columns.',
    AM1: 'Array Motor 1 (VFD).',
    AM2: 'Array Motor 2 (VFD).',
    TM1: 'Tilt Motor 1 (VFD).',
    TM2: 'Tilt Motor 2 (VFD).'
  };

  const vfdMetricTooltips = {
    Execution: 'Derived from Frequency: RUN if frequency > 0, otherwise STOP.',
    Health: 'Decoded from the 32-bit VFD state word flags (FOUND/WRONG_MODEL/INIT_FAIL/COMMS_FAIL).',
    Fault: 'Low 16 bits of VFD state word. 0 = OK, otherwise FAULT 0x____.',
    'State (raw)': 'Raw 32-bit VFD state word shown as hex for troubleshooting.',
    Frequency: 'Output frequency, scaled by 100 (raw/100 = Hz).',
    Current: 'Output current, scaled by 100 (raw/100 = A).',
    Voltage: 'Output voltage, scaled by 10 (raw/10 = V).',
    Temp: 'IGBT temperature, scaled by 10 (raw/10 = °C).'
  };

  const vfdExecution = (vfd) => {
    const f = toNumberOrNull(vfd?.frequency);
    if (f === null) return '—';
    return f > 0 ? 'RUN' : 'STOP';
  };

  const renderVfdSection = () => (
    <Box mb={0.5}>
      <Tooltip title="Section: VFD Status" arrow placement="top" enterDelay={250}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 0.25, fontSize: 13, lineHeight: 1.1, cursor: 'help' }}>
          VFD Status
        </Typography>
      </Tooltip>
      <Table
        size="small"
        sx={{
          '& td, & th': { py: 0.35, px: 0.75, fontSize: 12 },
          // make horizontal scan easier if the table overflows
          display: 'block',
          overflowX: 'auto',
          whiteSpace: 'nowrap'
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell
              sx={{
                fontWeight: 700,
                position: 'sticky',
                left: 0,
                zIndex: 3,
                backgroundColor: 'background.paper'
              }}
            >
              <Tooltip title={vfdHeaderTooltips.Metric} arrow placement="top" enterDelay={250}>
                <span style={{ cursor: 'help' }}>Metric</span>
              </Tooltip>
            </TableCell>
            {vfdMotors.map((m) => (
              <TableCell key={m.key} sx={{ fontWeight: 700 }}>
                <Tooltip title={vfdHeaderTooltips[m.label] || ''} arrow placement="top" enterDelay={250}>
                  <span style={{ cursor: 'help' }}>{m.label}</span>
                </Tooltip>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow sx={{ '& td': { fontWeight: 700 } }}>
            <TableCell
              sx={{
                color: 'text.secondary',
                position: 'sticky',
                left: 0,
                zIndex: 2,
                backgroundColor: 'background.paper'
              }}
            >
              <Tooltip title={vfdMetricTooltips.Execution} arrow placement="top" enterDelay={250}>
                <span style={{ cursor: 'help' }}>Execution</span>
              </Tooltip>
            </TableCell>
            {vfdMotors.map((m) => (
              <TableCell key={m.key}>{formatMaybe(vfdExecution(vfdStatus[m.key]))}</TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell
              sx={{
                color: 'text.secondary',
                position: 'sticky',
                left: 0,
                zIndex: 2,
                backgroundColor: 'background.paper'
              }}
            >
              <Tooltip title={vfdMetricTooltips.Health} arrow placement="top" enterDelay={250}>
                <span style={{ cursor: 'help' }}>Health</span>
              </Tooltip>
            </TableCell>
            {vfdMotors.map((m) => (
              <TableCell key={m.key}>{decodeVfdStateWord(vfdStatus[m.key]?.state).health}</TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell
              sx={{
                color: 'text.secondary',
                position: 'sticky',
                left: 0,
                zIndex: 2,
                backgroundColor: 'background.paper'
              }}
            >
              <Tooltip title={vfdMetricTooltips.Fault} arrow placement="top" enterDelay={250}>
                <span style={{ cursor: 'help' }}>Fault</span>
              </Tooltip>
            </TableCell>
            {vfdMotors.map((m) => (
              <TableCell
                key={m.key}
                sx={{
                  color: (decodeVfdStateWord(vfdStatus[m.key]?.state).fault !== 'OK') ? 'error.main' : 'text.primary',
                  fontWeight: (decodeVfdStateWord(vfdStatus[m.key]?.state).fault !== 'OK') ? 700 : 500
                }}
              >
                {decodeVfdStateWord(vfdStatus[m.key]?.state).fault}
              </TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell
              sx={{
                color: 'text.secondary',
                position: 'sticky',
                left: 0,
                zIndex: 2,
                backgroundColor: 'background.paper'
              }}
            >
              <Tooltip title={vfdMetricTooltips['State (raw)']} arrow placement="top" enterDelay={250}>
                <span style={{ cursor: 'help' }}>State (raw)</span>
              </Tooltip>
            </TableCell>
            {vfdMotors.map((m) => (
              <TableCell key={m.key}>{decodeVfdStateWord(vfdStatus[m.key]?.state).hex}</TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell
              sx={{
                color: 'text.secondary',
                position: 'sticky',
                left: 0,
                zIndex: 2,
                backgroundColor: 'background.paper'
              }}
            >
              <Tooltip title={vfdMetricTooltips.Frequency} arrow placement="top" enterDelay={250}>
                <span style={{ cursor: 'help' }}>Frequency</span>
              </Tooltip>
            </TableCell>
            {vfdMotors.map((m) => (
              <TableCell key={m.key}>{formatScaled(vfdStatus[m.key]?.frequency, 100, 'Hz')}</TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell
              sx={{
                color: 'text.secondary',
                position: 'sticky',
                left: 0,
                zIndex: 2,
                backgroundColor: 'background.paper'
              }}
            >
              <Tooltip title={vfdMetricTooltips.Current} arrow placement="top" enterDelay={250}>
                <span style={{ cursor: 'help' }}>Current</span>
              </Tooltip>
            </TableCell>
            {vfdMotors.map((m) => (
              <TableCell key={m.key}>{formatScaled(vfdStatus[m.key]?.current, 100, 'A')}</TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell
              sx={{
                color: 'text.secondary',
                position: 'sticky',
                left: 0,
                zIndex: 2,
                backgroundColor: 'background.paper'
              }}
            >
              <Tooltip title={vfdMetricTooltips.Voltage} arrow placement="top" enterDelay={250}>
                <span style={{ cursor: 'help' }}>Voltage</span>
              </Tooltip>
            </TableCell>
            {vfdMotors.map((m) => (
              <TableCell key={m.key}>{formatScaled(vfdStatus[m.key]?.voltage, 10, 'V')}</TableCell>
            ))}
          </TableRow>
          <TableRow>
            <TableCell
              sx={{
                color: 'text.secondary',
                position: 'sticky',
                left: 0,
                zIndex: 2,
                backgroundColor: 'background.paper'
              }}
            >
              <Tooltip title={vfdMetricTooltips.Temp} arrow placement="top" enterDelay={250}>
                <span style={{ cursor: 'help' }}>Temp</span>
              </Tooltip>
            </TableCell>
            {vfdMotors.map((m) => (
              <TableCell key={m.key}>{formatScaled(vfdStatus[m.key]?.temperature, 10, 'C')}</TableCell>
            ))}
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );

  // Wind Data - these come from sabalController.windData-ms
  const windData = sabalController['windData-ms'] || {};
  const windSpeedTooltip = (windowLabel) => `Wind speed (${windowLabel}). Raw is tenths of m/s (raw/10 = m/s). Displayed as m/s.`;
  const windDirTooltip = (windowLabel) => `Wind direction (${windowLabel}) in degrees (0–360). Observed: when raw > 360 it is degrees*10 (e.g. 3150 => 315.0°).`;
  const windFields = [
    { label: 'Speed 3s-m', value: formatWindSpeedMps(get(windData, ['speed 3s-m'], null)), tooltip: windSpeedTooltip('3-second avg') },
    { label: 'Speed 2m-m', value: formatWindSpeedMps(get(windData, ['speed 2m-m'], null)), tooltip: windSpeedTooltip('2-minute avg') },
    { label: 'Speed 5m-m', value: formatWindSpeedMps(get(windData, ['speed 5m-m'], null)), tooltip: windSpeedTooltip('5-minute avg') },
    { label: 'Speed 10m-m', value: formatWindSpeedMps(get(windData, ['speed 10m-m'], null)), tooltip: windSpeedTooltip('10-minute avg') },
    { label: 'Dir 3s-m', value: formatWindDirDeg(get(windData, ['dir 3s-m'], null)), tooltip: windDirTooltip('3-second avg') },
    { label: 'Dir 2m-m', value: formatWindDirDeg(get(windData, ['dir 2m-m'], null)), tooltip: windDirTooltip('2-minute avg') },
    { label: 'Dir 5m-m', value: formatWindDirDeg(get(windData, ['dir 5m-m'], null)), tooltip: windDirTooltip('5-minute avg') },
    { label: 'Dir 10m-m', value: formatWindDirDeg(get(windData, ['dir 10m-m'], null)), tooltip: windDirTooltip('10-minute avg') },
    { label: 'Gust', value: formatWindSpeedMps(get(windData, ['gust'], null)), tooltip: 'Peak wind speed (gust). Raw is tenths of m/s (raw/10 = m/s). Displayed as m/s.' },
    { label: 'Gust Dir', value: formatWindDirDeg(get(windData, ['gustdir'], null)), tooltip: 'Direction of the peak gust, in degrees (0–360). If raw > 360, treat as degrees*10.' },
    { label: 'Gustier 5m-m', value: formatWindSpeedMps(get(windData, ['gustier 5m-m'], null)), tooltip: '5-minute gust average. Raw is tenths of m/s (raw/10 = m/s). Displayed as m/s.' },
    { label: 'Limits', value: formatWindLimits(get(windData, ['limits'], null)), tooltip: 'Wind limit flags. 0=Normal, 1=Above Flat Limit, 2=Above Pack Limit.' },
    {
      label: 'Sensor',
      value: (() => {
        const decoded = decodeAnemometerStateWord(get(windData, ['sensor'], null));
        if (decoded.text === '—') return '—';
        return `${decoded.text} (${decoded.hex})`;
      })(),
      tooltip: 'Wind sensor/anemometer state flags (32-bit). Matches controller FOUND/NOT_FOUND/LOST_COMMS flags.'
    },
  ];

  // Encoder Status - these come from sabalController.encoderStatus
  // Some firmware variants (and older telemetry formats) may use different keys/field names.
  // Prefer encoderStatus (controller MQTT format), but fall back to encoders.
  const encoderStatus = sabalController.encoderStatus || sabalController.encoders || sabalController.encoder_status || {};

  const formatEncoderStatus = (enc) => {
    const raw = get(enc, ['status', 'state'], null);
    const decoded = decodeEncoderStateWord(raw);
    // Show a compact, readable status string; keep raw hex visible for troubleshooting.
    if (decoded.health === '—' && decoded.fault === '—') return '—';
    if (decoded.fault !== 'OK') return `${decoded.health} • ${decoded.fault} (${decoded.hex})`;
    return `${decoded.health} (${decoded.hex})`;
  };

  const encoderFields = [
    { label: 'AM1 Status', value: formatEncoderStatus(encoderStatus.am1), valueSx: { fontWeight: 700 }, tooltip: 'AM1 encoder status flags (32-bit). Shows decoded flags + raw hex.' },
    { label: 'AM1 Distance', value: formatInches100(get(encoderStatus.am1, ['distance'])), tooltip: 'AM1 encoder distance. Raw is scaled by 100 (raw/100). Units: inches.' },
    { label: 'AM1 Position', value: get(encoderStatus.am1, ['position']), tooltip: 'AM1 encoder raw position counts (signed 32-bit).' },
    { label: 'AM1 Temperature', value: get(encoderStatus.am1, ['temperature']), tooltip: 'AM1 encoder temperature, scaled by 10 (raw/10 = °C).' },
    { label: 'AM2 Status', value: formatEncoderStatus(encoderStatus.am2), valueSx: { fontWeight: 700 }, tooltip: 'AM2 encoder status flags (32-bit). Shows decoded flags + raw hex.' },
    { label: 'AM2 Distance', value: formatInches100(get(encoderStatus.am2, ['distance'])), tooltip: 'AM2 encoder distance. Raw is scaled by 100 (raw/100). Units: inches.' },
    { label: 'AM2 Position', value: get(encoderStatus.am2, ['position']), tooltip: 'AM2 encoder raw position counts (signed 32-bit).' },
    { label: 'AM2 Temperature', value: get(encoderStatus.am2, ['temperature']), tooltip: 'AM2 encoder temperature, scaled by 10 (raw/10 = °C).' },
    { label: 'TM1 Status', value: formatEncoderStatus(encoderStatus.tm1), valueSx: { fontWeight: 700 }, tooltip: 'TM1 encoder status flags (32-bit). Shows decoded flags + raw hex.' },
    { label: 'TM1 Angle', value: formatDegrees100(get(encoderStatus.tm1, ['angle', 'distance'])), tooltip: 'TM1 encoder tilt angle, scaled by 100 (raw/100 = degrees).' },
    { label: 'TM1 Position', value: get(encoderStatus.tm1, ['position']), tooltip: 'TM1 encoder raw position counts (signed 32-bit).' },
    { label: 'TM1 Temperature', value: get(encoderStatus.tm1, ['temperature']), tooltip: 'TM1 encoder temperature, scaled by 10 (raw/10 = °C).' },
    { label: 'TM2 Status', value: formatEncoderStatus(encoderStatus.tm2), valueSx: { fontWeight: 700 }, tooltip: 'TM2 encoder status flags (32-bit). Shows decoded flags + raw hex.' },
    { label: 'TM2 Angle', value: formatDegrees100(get(encoderStatus.tm2, ['angle', 'distance'])), tooltip: 'TM2 encoder tilt angle, scaled by 100 (raw/100 = degrees).' },
    { label: 'TM2 Position', value: get(encoderStatus.tm2, ['position']), tooltip: 'TM2 encoder raw position counts (signed 32-bit).' },
    { label: 'TM2 Temperature', value: get(encoderStatus.tm2, ['temperature']), tooltip: 'TM2 encoder temperature, scaled by 10 (raw/10 = °C).' },
  ];

  // Doors Status - these come from sabalController.Doors
  const doors = sabalController.Doors || {};
  const doorsFields = [
    { label: 'Door State', value: get(doors, ['doorState']), tooltip: 'Door state enum (controller value).' },
    { label: 'Door Pos 1', value: get(doors, ['Door Pos 1']), tooltip: 'Door position 1, scaled by 100 (raw/100).' },
    { label: 'Door Pos 2', value: get(doors, ['Door Pos 2']), tooltip: 'Door position 2, scaled by 100 (raw/100).' },
    { label: 'Door Pos 3', value: get(doors, ['Door Pos 3']), tooltip: 'Door position 3, scaled by 100 (raw/100).' },
    { label: 'Door Pos 4', value: get(doors, ['Door Pos 4']), tooltip: 'Door position 4, scaled by 100 (raw/100).' },
    { label: 'Door CtrlStat', value: get(doors, ['Door CtrlStat']), tooltip: 'Door control status (controller value).' },
  ];

  // Tilt Status - these come from sabalController.Tilt
  const tilt = sabalController.Tilt || {};
  const tiltFields = [
    { label: 'Current Tracking Angle', value: formatDegrees100(get(tilt, ['current tracking angle'])), tooltip: 'Current tracking angle, scaled by 100 (raw/100 = degrees).' },
    { label: 'Tilt Angle Min', value: formatDegrees100(get(tilt, ['tilt angle min'])), tooltip: 'Minimum allowed tilt angle, scaled by 100 (raw/100 = degrees).' },
    { label: 'Tilt Angle Max', value: formatDegrees100(get(tilt, ['tilt angle max'])), tooltip: 'Maximum allowed tilt angle, scaled by 100 (raw/100 = degrees).' },
    { label: 'Track Sunrise', value: formatSecsSinceMidnightToTime(get(tilt, ['Track Sunrise secs']), record?.timestamp), tooltip: 'Sunrise time for tracking. Raw is seconds since midnight UTC; displayed as America/Nassau local time.' },
    { label: 'Track Sunset', value: formatSecsSinceMidnightToTime(get(tilt, ['Track Sunset secs']), record?.timestamp), tooltip: 'Sunset time for tracking. Raw is seconds since midnight UTC; displayed as America/Nassau local time.' },
    { label: 'Tracking Start', value: formatSecsSinceMidnightToTime(get(tilt, ['Tracking Start secs']), record?.timestamp), tooltip: 'Tracking start time. Raw is seconds since midnight UTC; displayed as America/Nassau local time.' },
    { label: 'Tracking End', value: formatSecsSinceMidnightToTime(get(tilt, ['Tracking End secs']), record?.timestamp), tooltip: 'Tracking end time. Raw is seconds since midnight UTC; displayed as America/Nassau local time.' },
    { label: 'Current Time', value: formatSecsSinceMidnightToTime(get(tilt, ['Current Secs']), record?.timestamp), tooltip: 'Controller “current seconds since midnight UTC” rendered as America/Nassau local time.' },
  ];

  // Error Log - these come from sabalController.errorLog
  const errorLog = sabalController.errorLog || {};
  const errorFields = [
    { label: 'Last Error', value: get(errorLog, ['lastError']), tooltip: 'Most recent error message string.' },
    { label: 'Error Count', value: get(errorLog, ['errorCount']), tooltip: 'Count of errors recorded by the controller.' },
    { label: 'Last Error Time', value: get(errorLog, ['lastErrorTime']), tooltip: 'UTC timestamp for last error (may be empty if none recorded).' },
  ];

  // Energy / Inverter fields (v2 canopy.telemetry)
  const energySection = sabalController.energy || null;
  const inverter = energySection?.inverter || {};
  const acOutput = inverter.acOutput || {};
  const productionRegisters = energySection?.production?.inverterRegisters || {};
  const formatUnitValue = (raw, unit, digits = 2) => {
    const n = toNumberOrNull(raw);
    if (n === null) return '—';
    const formatted = Number.isInteger(n) ? String(n) : n.toFixed(digits).replace(/\.00$/, '').replace(/(\.\d)0$/, '$1');
    return `${formatted} ${unit}`;
  };
  const energyFields = energySection ? [
    { label: 'Inverter Status', value: get(inverter, ['status']), tooltip: 'Source: sabalController.energy.inverter.status' },
    { label: 'AC Power', value: formatUnitValue(toNumberOrNull(acOutput.powerW) !== null ? toNumberOrNull(acOutput.powerW) / 1000 : null, 'kW', 2), tooltip: 'Source: sabalController.energy.inverter.acOutput.powerW (displayed in kW)' },
    { label: 'AC Voltage', value: formatUnitValue(get(acOutput, ['voltageV', 'lineToLineVoltageV'], null), 'V', 1), tooltip: 'Source: sabalController.energy.inverter.acOutput.voltageV' },
    { label: 'AC Current', value: formatUnitValue(get(acOutput, ['currentA'], null), 'A', 2), tooltip: 'Source: sabalController.energy.inverter.acOutput.currentA' },
    { label: 'AC Frequency', value: formatUnitValue(get(acOutput, ['frequencyHz'], null), 'Hz', 2), tooltip: 'Source: sabalController.energy.inverter.acOutput.frequencyHz' },
    { label: 'Inverter Temp', value: formatUnitValue(inverter.temperatures?.deviceC, '°C', 1), tooltip: 'Source: sabalController.energy.inverter.temperatures.deviceC' },
    { label: 'Today Energy', value: formatUnitValue(productionRegisters.todayKwh, 'kWh', 2), tooltip: 'Source: sabalController.energy.production.inverterRegisters.todayKwh' },
    { label: 'Month Energy', value: formatUnitValue(productionRegisters.monthKwh, 'kWh', 1), tooltip: 'Source: sabalController.energy.production.inverterRegisters.monthKwh' },
    { label: 'Year Energy', value: formatUnitValue(productionRegisters.yearKwh, 'kWh', 1), tooltip: 'Source: sabalController.energy.production.inverterRegisters.yearKwh' },
    { label: 'Lifetime Energy', value: formatUnitValue(productionRegisters.lifetimeKwh, 'kWh', 3), tooltip: 'Source: sabalController.energy.production.inverterRegisters.lifetimeKwh' },
  ] : null;

  // Helper to render a section in N columns
  const renderSection = (title, fields) => (
    <Box mb={0.5}>
      <Tooltip title={`Section: ${title}`} arrow placement="top" enterDelay={250}>
        <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mb: 0.25, fontSize: 13, lineHeight: 1.1, cursor: 'help' }}>{title}</Typography>
      </Tooltip>
      <Grid container spacing={0}>
        {fields.map((f, idx) => <DataGridCell key={f.label + idx} {...f} columns={responsiveColumns} />)}
      </Grid>
    </Box>
  );

  // Layout
  const content = (
    <Paper sx={{ p: isPhone ? 1 : 1.25, maxWidth: 900, width: '100%', mx: 'auto', my: 1, position: 'relative', fontFamily: 'inherit', maxHeight: '90vh', overflowY: 'auto' }} elevation={8}>
      {modal && (
        <IconButton onClick={onClose} sx={{ position: 'absolute', top: 4, right: 4, p: 0.5 }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      )}
      {/* Header Fields */}
      <Box mb={0.5}>
        <Tooltip title="Section: Telemetry Record" arrow placement="top" enterDelay={250}>
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 0.25, fontSize: 14, lineHeight: 1.1, cursor: 'help' }}>Telemetry Record</Typography>
        </Tooltip>
        <Grid container spacing={0}>
          {headerFields.map((f, idx) => <DataGridCell key={f.label + idx} {...f} columns={responsiveColumns} />)}
        </Grid>
      </Box>
      <Divider sx={{ mb: 0.5 }} />
      {renderSection('System Info', systemInfoFields)}
      {energyFields && (
        <>
          <Divider sx={{ mb: 0.5 }} />
          {renderSection('Inverter & Solar Production', energyFields)}
        </>
      )}
      <Divider sx={{ mb: 0.5 }} />
      {renderVfdSection()}
      <Divider sx={{ mb: 0.5 }} />
      {renderSection('Encoder Status', encoderFields)}
      <Divider sx={{ mb: 0.5 }} />
      {renderSection('Doors', doorsFields)}
      <Divider sx={{ mb: 0.5 }} />
      {renderSection('Tilt', tiltFields)}
      <Divider sx={{ mb: 0.5 }} />
      {renderSection('Wind Data', windFields)}
      <Divider sx={{ mb: 0.5 }} />
      {renderSection('Error Log', errorFields)}
    </Paper>
  );

  if (modal) {
    if (!open) return null;
    return (
      <Box sx={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', bgcolor: 'rgba(0,0,0,0.3)', zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={onClose}>
        <Box onClick={e => e.stopPropagation()}>{content}</Box>
      </Box>
    );
  }
  // Static panel
  return <Box>{content}</Box>;
}

export default Datalens; 